import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { useLazyInitCruiseQuery } from "../../../store/services/SearchService";
import {
  Room,
  RoomsState,
  updateRoomsState,
} from "../../../store/slices/roomsSlice";
import { ICruise, Market } from "../../../store/slices/searchSlice";
import { useTypedDispatch, useTypedSelector } from "../../../store/store";
import SearchBar from "../../base/SearchBar";
import CustomBreadcrumbs from "../../shared/CustomBreadcrumbs";

import styles from "./index.module.scss";

const AVAILABLE_MARKETS: Array<keyof ICruise["markets"]> = [
  "inside",
  "outside",
  "balcony",
  "suite",
];

const createArrayRange = (start: number, end: number) => {
  const resultArray = [...Array(end - start + 1)].map(
    (_, index) => start + index,
  );

  return resultArray;
};

function SearchLayout({ children }: React.PropsWithChildren) {
  const dispatch = useTypedDispatch();
  const [getCruise] = useLazyInitCruiseQuery();

  const { cruiseId } = useParams();
  const [searchParams] = useSearchParams();

  const { cruise } = useTypedSelector((state) => state.search);
  const { rooms } = useTypedSelector((state) => state.rooms);

  const { api_language: language } = useTypedSelector(
    (state) => state.environment,
  );

  const totalRooms = +(searchParams.get("rooms") ?? 0);
  const source = searchParams.get("source") ?? "";
  const residency = searchParams.get("residency") ?? "";

  const initCruise = () => {
    getCruise({
      language,
      cruiseId: cruiseId as string,
      source,
      residency,
    });
  };

  const initBlankStaterooms = () => {
    const roomsRange = createArrayRange(1, totalRooms);
    const defaultMarketType = AVAILABLE_MARKETS.find(
      (mKey) => (cruise?.markets[mKey] as Market).price !== "0",
    );

    const initialRoomsState: RoomsState & { rooms: Record<number, Room> } = {
      rooms: {},
      cabins: undefined,
      isCabinsLoading: true,
    };

    roomsRange.forEach((num) => {
      const room = rooms?.[num] ?? {};

      const paramsFare = searchParams.get(`fare_${num}`);
      const paramsGrade = searchParams.get(`grade_${num}`);
      const paramsGuests = searchParams.get(`guests_${num}`);
      const paramsMarket = searchParams.get(`market_${num}`);

      const fare = cruise?.pricing.find((el) => el.rate_code === paramsFare);
      const grade = cruise?.ship.grades.find((el) => el.code === paramsGrade);
      const guestsNumber = paramsGuests ? +paramsGuests : undefined;
      const marketType = paramsMarket;

      initialRoomsState.rooms[num] = {
        ...room,
        fare: fare ?? cruise?.pricing.find((el) => el.lowest_fare),
        grade,
        guestsNumber,
        marketType: marketType ?? defaultMarketType,
      };
    });

    dispatch(updateRoomsState(initialRoomsState));
  };

  useEffect(() => {
    if (cruiseId) {
      initCruise();
    }
  }, [cruiseId, residency]);

  useEffect(() => {
    if (cruise) {
      initBlankStaterooms();
    }
  }, [cruise, totalRooms]);

  return (
    <div>
      <SearchBar />
      <main className={styles.container}>
        <section className={styles.content}>
          <CustomBreadcrumbs />

          <div>{children}</div>
        </section>
      </main>
    </div>
  );
}

export default SearchLayout;
